import { Button, Checkbox, Input, Radio } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { EditOutlined } from "@ant-design/icons";
import {
  dataLayerClickedEvent,
  dataLayerViewedEvent,
  tagManagerEvent,
} from "event-tracker/gtm-events";
import { QuizButtonClickEventName } from "event-tracker/button-click-event-name";
import { fetchInstagramList } from "shared/service/StrmService";
import snakeToCamelCase from "shared/helpers/snakeToCamelCase";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInstagram } from "@fortawesome/free-brands-svg-icons";
import InstagramMockup from "shared/assets/images/instagram-mockup.png";

const defaultArtistImage =
  "https://placehold.co/100x100/292929/f2f2f2?text=Artist";

// const ArtistNotFoundImage =
//   "https://placehold.co/100x100/E64B4B/f2f2f2?text=Artist+not+found";

enum Step {
  Search = "SEARCH",
  Select = "SELECT",
}

type Result = {
  name?: string;
  id?: string;
  avatarUrl?: string;
};

type InstagramSearchProps = {
  value?: Result;
  onChange?: (option?: Result) => void;
  onClickNext?: () => void;
};

const InstagramSearch: React.FC<InstagramSearchProps> = (props) => {
  const { value, onChange = (_: any) => {}, onClickNext } = props;
  const { t } = useTranslation();
  const [fieldValue, setFieldValue] = useState<string>("");
  const [searchField, setSearchField] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [step, setStep] = useState<Step>(Step.Search);
  const [results, setResults] = useState<Result[]>([]);
  const prevSearchFieldRef = useRef<string>("");

  const getInstagramArtists = useCallback(async () => {
    if (!searchField || searchField === prevSearchFieldRef.current) return;

    prevSearchFieldRef.current = searchField;

    dataLayerClickedEvent(QuizButtonClickEventName.SEARCH_INSTAGRAM_PROFILE, {
      ...tagManagerEvent.Quiz.S4A_QUIZ_PLATFORMS_CONNECT,
    });

    setLoading(true);

    try {
      const response = await fetchInstagramList(
        encodeURIComponent(searchField.trim())
      );
      const service = snakeToCamelCase(response);

      const result = service.map((profile: any) => ({
        id: profile.searchByUsername,
        name: profile.profile?.artistName,
        avatarUrl: profile.profile?.pictureUrl,
      }));

      if (result.length === 0) {
        onChange({
          id: "no-data",
          name: t("quiz.pages.Questionario.instagramUserNotFound"),
        });
        return;
      }

      if (result.length === 1) {
        const profile = result[0];
        const { serviceAvailable, profileFound } = service[0];

        if (!profileFound) {
          onChange({
            id: "no-data",
            name: t("quiz.pages.Questionario.instagramUserNotFound"),
          });

          return;
        }

        if (!serviceAvailable) {
          setResults([{ name: searchField, id: searchField }]);
          setStep(Step.Select);
        }

        onChange(profile);
        return;
      }

      setResults(result);
      setStep(Step.Select);
    } catch (error) {
      console.error("Error fetching Instagram artists:", error);
      onChange({
        id: "no-data",
        name: t("quiz.pages.Questionario.instagramUserNotFound"),
      });
    } finally {
      setLoading(false);
    }
  }, [searchField, t, onChange]);

  const onClickContinue = () => {
    if (onClickNext) onClickNext();
  };

  const onSearch = () => {
    if (!fieldValue.trim()) return;
    if (searchField?.toLowerCase() === fieldValue?.toLowerCase()) return;
    setSearchField(fieldValue?.toLowerCase());
  };

  const onKeyPress = (event: any) => {
    if (event.which === 13) {
      onSearch();
    }
  };

  const onSelectArtist = (option?: Result) => {
    onChange(option);
  };

  const onClickEdit = () => {
    setStep(Step.Search);
    onSelectArtist();
  };

  const onClickNoValue = (checked: boolean) => {
    if (checked) {
      const noDataValue = { id: "no-data" };
      onChange(noDataValue);
      setSearchField("");
      // setSelectedOption(noDataValue);
      // onSelectCallback(false);
    } else {
      onChange();
      // setSelectedOption(undefined);
    }
  };

  useEffect(() => {
    getInstagramArtists();
  }, [getInstagramArtists]);

  useEffect(() => {
    if (step === Step.Search) {
      dataLayerViewedEvent({
        ...tagManagerEvent.FreeAssessment.INSTAGRAM_SEARCH_NEW_ASSESSMENT,
        version: "V3",
      });
      return;
    }
  }, [step]);

  const Footer = () => {
    return (
      <div className="footer">
        <Button
          className="botao-roxo"
          onClick={onClickContinue}
          disabled={!value}
        >
          {t("quiz.pages.FreeAssessment.continue")}
        </Button>
      </div>
    );
  };

  if (step === Step.Select)
    return (
      <>
        <h1>
          {t("quiz.pages.FreeAssessment.results", {
            artistsCount: results.length,
          })}
        </h1>
        <div className="search-value">
          <span>{searchField}</span>
          <EditOutlined onClick={onClickEdit} />
        </div>
        <div className="full-item results">
          {results.map((result) => (
            <div
              key={result.id}
              onClick={() => onSelectArtist(result)}
              className="result-item"
            >
              <img
                src={result.avatarUrl ?? defaultArtistImage}
                alt={result.name}
                className="image"
                onError={(e) => (e.currentTarget.src = defaultArtistImage)}
              />
              <span className="name">{result.name}</span>
              <Radio checked={result.id === value?.id}></Radio>
            </div>
          ))}
          {/* <div style={{ display: "flex", alignItems: "center", marginTop: 20 }}>
            <span className="help-tip">
              {t("quiz.pages.FreeAssessment.didNotFoundProfile")}{" "}
              <b
                onClick={() => {
                  setFieldValue("");
                  setStep(Step.Help);
                }}
              >
                {t("quiz.pages.FreeAssessment.clickHere")}
              </b>
            </span>
          </div> */}
        </div>
        <Footer />
      </>
    );

  return (
    <>
      <FontAwesomeIcon icon={faInstagram} className="label-icon" />
      <h1>{t("quiz.pages.FreeAssessment.instagramSearch")}</h1>
      {value?.avatarUrl && (
        <div className="profile-image">
          <img src={value?.avatarUrl} className="image" alt="artist" />
        </div>
      )}
      <span>{value?.name}</span>
      <div className="full-item">
        <div className="caixa-input">
          <Input
            placeholder={t("quiz.pages.Questionario.searchPlaceholder")}
            onChange={(event) => setFieldValue(event.target.value)}
            onKeyPress={onKeyPress}
            style={{ marginTop: 14 }}
            value={fieldValue}
            prefix="@"
            suffix={
              <Button
                className="botao-roxo"
                style={{ padding: 0, borderRadius: 2 }}
                onClick={onSearch}
                htmlType="button"
                loading={loading}
              >
                {t("quiz.pages.Questionario.buscar")}
              </Button>
            }
          />
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginTop: 6,
          }}
        >
          <Checkbox
            onChange={({ target: { checked } }) => onClickNoValue(checked)}
            checked={value?.id === "no-data"}
          >
            <span className="not-present">
              {t("quiz.pages.Questionario.noInstagram")}
            </span>
          </Checkbox>
        </div>

        <div className="field-tip">
          <h3>{t("quiz.pages.FreeAssessment.havingProblem")}</h3>
          <div className="info">
            <div className="help-box">
              <p>{t("quiz.pages.FreeAssessment.instagramTip")}</p>
            </div>

            <div className="spotify-card">
              <img src={InstagramMockup} alt="Henrique & Juliano" />
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default InstagramSearch;
